<script setup>
import { ref, onMounted, onBeforeUnmount, watch, nextTick } from 'vue';
import { useBlockSafe } from '@/composables/useBlockSafe';
const props = defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
});
const {
  id,
  backgroundColor,
  spacingTop,
  spacingBottom,
  title,
  items,
  hasTitle,
  hasItems,
  hasAnyContent,
} = useBlockSafe(props.data);

const isPdfUrl = (url) => {
  if (typeof url !== 'string') return false;
  const cleanUrl = url.split('#')[0].split('?')[0];
  return /\.pdf$/i.test(cleanUrl);
};

const activeTab = ref(items.value[0]?.id);
watch(activeTab, async () => {
  await nextTick();
  window.dispatchEvent(new Event('resize'));
});

const tabListElement = useTemplateRef('tabList');

const onTabListResize = () => {
  tabListElement.value?.updateInkBar();
};
onMounted(() => {
  window.addEventListener('resize', onTabListResize, { passive: true });
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', onTabListResize);
});
</script>
<template>
  <section
    v-if="hasAnyContent"
    :id="id"
    class="section"
    :class="[
      backgroundColor ? `bg-${backgroundColor}` : '',
      backgroundColor === 'black' ? 'white' : 'black',
      spacingTop ? `section--top-${spacingTop}` : 'section--top-xl',
      spacingBottom ? `section--bottom-${spacingBottom}` : 'section--bottom-xl',
    ]"
    data-aos="fade-up"
    data-aos-easing="ease-out-cubic"
  >
    <div class="container">
      <div class="iframe">
        <h2
          v-if="hasTitle"
          class="mb-0 iframe__title h2 text-center"
          data-aos="fade-up"
          data-aos-easing="ease-out-cubic"
          v-html="title"
        ></h2>
        <Tabs
          v-if="hasItems"
          v-model:value="activeTab"
          :scrollable="false"
          class="iframe__tabs"
          data-aos="fade-up"
          data-aos-easing="ease-out-cubic"
        >
          <TabList
            v-if="items[0]?.title"
            ref="tabList"
            :class="`iframe__tabs-headers tabs-bg-${backgroundColor}`"
          >
            <Tab
              v-for="tab in items"
              :key="tab.id"
              :value="tab.id"
              :class="[
                `iframe__tabs-title iframe__tabs-title--${backgroundColor}`,
              ]"
            >
              {{ tab.title }}
            </Tab>
          </TabList>
          <TabPanels class="iframe__tabs-content">
            <TabPanel
              v-for="tab in items"
              :key="tab.id"
              :value="tab.id"
              class="iframe__tab"
            >
              <div v-if="isPdfUrl(tab.iframe)" class="iframe__pdf">
                <object
                  :data="tab.iframe"
                  type="application/pdf"
                  class="iframe__pdf-object"
                >
                  <p class="iframe__pdf-fallback">
                    {{ $t('iframe.pdf_fallback') }}
                    <a :href="tab.iframe" target="_blank" rel="noopener">{{
                      $t('iframe.open_pdf')
                    }}</a>
                  </p>
                </object>
              </div>
              <iframe v-else :src="tab.iframe" class="iframe__tab-frame">
              </iframe>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
@use 'sass:math';
.iframe {
  display: flex;
  flex-direction: column;
  gap: 80px;
  @include media-breakpoint-up(xl) {
    @include responsive-spacing(gap, 80);
  }
  &__title {
    margin: 0;
  }
  &__tabs {
    display: flex;
    flex-direction: column;
    gap: 32px;
    @include media-breakpoint-up(sm) {
      gap: 48px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 48);
    }
    &-headers {
      display: flex;
      justify-content: center;
      position: relative;
      &:deep(.p-tablist-tab-list) {
        justify-content: center;
      }
      &:deep(.p-tablist-active-bar) {
        height: 4px;
      }
      @include media-breakpoint-up(xl) {
        &:deep(.p-tablist-active-bar) {
          @include responsive-spacing(height, 4);
        }
      }
    }
    &-title {
      @include link-normal;
      border-bottom: 1px solid $divider;
      color: $on-surface-variant-2;
      cursor: pointer;
      position: relative;
      padding: 16px;
      text-align: center;
      transition: color 0.2s ease;
      white-space: unset;
      width: auto;
      &:only-child {
        cursor: default;
      }
      @include media-breakpoint-up(sm) {
        padding: 17px 16px 15px;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(border-bottom-width, 1);
        @include responsive-spacing(padding-top, 17);
        @include responsive-spacing(padding-right, 16);
        @include responsive-spacing(padding-left, 16);
        @include responsive-spacing(padding-bottom, 15);
      }
      &[data-p='active'] {
        color: $on-surface;
      }
      &--white,
      &--gray {
        color: $on-surface-variant-2;
      }
      &--black {
        color: $on-surface-variant-2;
        &[data-p='active'] {
          color: $white;
        }
      }
    }
    &-content {
      background-color: $white;
      border-radius: 8px;
      //min-height: 688px;
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(border-radius, 8);
        //@include responsive-spacing(min-height, 688);
      }
    }
  }
  &__tab-frame {
    aspect-ratio: 16 / 9;
    border: none;
    width: 100%;
  }
  &__pdf {
    display: flex;
    flex-direction: column;
    gap: 16px;
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 16);
    }
    &-object {
      aspect-ratio: 1 / 1.414;
      border: none;
      width: 100%;
    }
    &-fallback {
      align-items: center;
      display: flex;
      gap: 8px;
      justify-content: center;
      padding: 24px;
      text-align: center;
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(gap, 8);
        @include responsive-spacing(padding, 24);
      }
    }
  }
}
</style>
